import type {
  ApmBrand,
  CardBrand,
  PaymentBrand,
  SupportedWalletBadgeBrand,
  WalletBrand,
} from "./ports";

export const PAYMENT_ICON_BASE_URL =
  "https://assets.fluid.app/images/payment_icons";
export const GENERIC_PAYMENT_ICON_URL = `${PAYMENT_ICON_BASE_URL}/generic.svg`;

const CARD_BRANDS = new Set<PaymentBrand>([
  "visa",
  "mastercard",
  "amex",
  "discover",
  "jcb",
]);

const WALLET_BRANDS = new Set<PaymentBrand>([
  "apple_pay",
  "google_pay",
  "samsung_pay",
]);

const SUPPORTED_WALLET_BADGE_BRANDS = new Set<PaymentBrand>([
  "apple_pay",
  "google_pay",
]);

const CARD_BRAND_NAME: Record<CardBrand, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  jcb: "JCB",
};

const WALLET_BRAND_NAME: Record<WalletBrand, string> = {
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  samsung_pay: "Samsung Pay",
};

const APM_BRAND_NAME: Record<ApmBrand, string> = {
  paypal: "PayPal",
  venmo: "Venmo",
  klarna: "Klarna",
  affirm: "Affirm",
  afterpay: "Afterpay",
  bread: "Bread",
  skrill: "Skrill",
  ideal: "iDEAL",
  bancontact: "Bancontact",
  eps: "EPS",
  blik: "BLIK",
  trustly: "Trustly",
  oxxo: "OXXO",
  pix: "Pix",
  yape: "Yape",
  satispay: "Satispay",
  alipay: "Alipay",
  unionpay: "UnionPay",
  gcash: "GCash",
  grabpay: "GrabPay",
  maya: "Maya",
  upi: "UPI",
};

const BRAND_ALIASES: Record<string, PaymentBrand> = {
  visa: "visa",
  mastercard: "mastercard",
  master: "mastercard",
  "master card": "mastercard",
  mc: "mastercard",
  amex: "amex",
  "american express": "amex",
  americanexpress: "amex",
  discover: "discover",
  disc: "discover",
  jcb: "jcb",
  paypal: "paypal",
  venmo: "venmo",
  "apple pay": "apple_pay",
  applepay: "apple_pay",
  "google pay": "google_pay",
  googlepay: "google_pay",
  "samsung pay": "samsung_pay",
  samsungpay: "samsung_pay",
  klarna: "klarna",
  affirm: "affirm",
  afterpay: "afterpay",
  clearpay: "afterpay",
  bread: "bread",
  skrill: "skrill",
  ideal: "ideal",
  bancontact: "bancontact",
  eps: "eps",
  blik: "blik",
  trustly: "trustly",
  oxxo: "oxxo",
  pix: "pix",
  yape: "yape",
  satispay: "satispay",
  alipay: "alipay",
  unionpay: "unionpay",
  gcash: "gcash",
  grabpay: "grabpay",
  maya: "maya",
  paymaya: "maya",
  upi: "upi",
  "upi india": "upi",
};

const BRAND_ASSET_SLUG: Record<PaymentBrand, string> = {
  visa: "visa",
  mastercard: "master",
  amex: "american_express",
  discover: "discover",
  jcb: "jcb",
  apple_pay: "apple_pay",
  google_pay: "google_pay",
  samsung_pay: "samsung_pay",
  paypal: "paypal",
  venmo: "venmo",
  klarna: "klarna",
  affirm: "affirm",
  afterpay: "afterpay",
  bread: "bread",
  skrill: "skrill",
  ideal: "ideal",
  bancontact: "bancontact",
  eps: "eps",
  blik: "blik",
  trustly: "trustly",
  oxxo: "oxxo",
  pix: "pix",
  yape: "yape",
  satispay: "satispay",
  alipay: "alipay",
  unionpay: "unionpay",
  gcash: "gcash",
  grabpay: "grabpay",
  maya: "maya",
  upi: "upi",
};

export function normalizeBrand(
  raw: string | null | undefined,
): PaymentBrand | null {
  const key = normalizeBrandKey(raw);
  if (!key) return null;
  return BRAND_ALIASES[key] ?? null;
}

export function brandIconUrl(brand: string | null | undefined): string {
  const canonical = normalizeBrand(brand);
  if (!canonical) return GENERIC_PAYMENT_ICON_URL;
  return `${PAYMENT_ICON_BASE_URL}/${BRAND_ASSET_SLUG[canonical]}.svg`;
}

export function getPaymentBrandName(
  brand: string | null | undefined,
): string | null {
  const canonical = normalizeBrand(brand);
  if (!canonical) return null;
  const cardBrand = normalizeCardBrand(canonical);
  if (cardBrand) return CARD_BRAND_NAME[cardBrand];
  const walletBrand = normalizeWalletBrand(canonical);
  if (walletBrand) return WALLET_BRAND_NAME[walletBrand];
  const apmBrand = normalizeApmBrand(canonical);
  return apmBrand ? APM_BRAND_NAME[apmBrand] : null;
}

export function getCardBrandName(
  brand: string | null | undefined,
): string | null {
  const canonical = normalizeCardBrand(brand);
  return canonical ? CARD_BRAND_NAME[canonical] : null;
}

export function apmBrandName(raw: string | null | undefined): string | null {
  const canonical = normalizeApmBrand(raw);
  return canonical ? APM_BRAND_NAME[canonical] : null;
}

export function normalizeCardBrand(
  brand: string | null | undefined,
): CardBrand | null {
  const canonical = normalizeBrand(brand);
  return canonical != null && CARD_BRANDS.has(canonical)
    ? (canonical as CardBrand)
    : null;
}

export function normalizeWalletBrand(
  brand: string | null | undefined,
): WalletBrand | null {
  const canonical = normalizeBrand(brand);
  return canonical != null && WALLET_BRANDS.has(canonical)
    ? (canonical as WalletBrand)
    : null;
}

export function normalizeSupportedWalletBadgeBrand(
  brand: string | null | undefined,
): SupportedWalletBadgeBrand | null {
  const canonical = normalizeBrand(brand);
  return canonical != null && SUPPORTED_WALLET_BADGE_BRANDS.has(canonical)
    ? (canonical as SupportedWalletBadgeBrand)
    : null;
}

export function normalizeApmBrand(
  brand: string | null | undefined,
): ApmBrand | null {
  const canonical = normalizeBrand(brand);
  return canonical != null && canonical in APM_BRAND_NAME
    ? (canonical as ApmBrand)
    : null;
}

export function isCardBrand(brand: string | null | undefined): boolean {
  return normalizeCardBrand(brand) != null;
}

export function isWalletBrand(brand: string | null | undefined): boolean {
  return normalizeWalletBrand(brand) != null;
}

export function isSupportedWalletBadgeBrand(
  brand: string | null | undefined,
): boolean {
  return normalizeSupportedWalletBadgeBrand(brand) != null;
}

export function isApmBrand(brand: string | null | undefined): boolean {
  return normalizeApmBrand(brand) != null;
}

export function isGenericCardLabel(value: string | null | undefined): boolean {
  const key = normalizeBrandKey(value)?.replace(/\s+/g, "_");
  return key === "card" || key === "credit_card";
}

function normalizeBrandKey(raw: string | null | undefined): string | null {
  const key = raw
    ?.trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
  return key ? key : null;
}
